import React, { useState, useEffect } from 'react';
import { Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, TableSortLabel, CircularProgress, Box, Collapse, IconButton, Typography, Tooltip } from '@mui/material';
import { TabPanel } from '@mui/lab';
import { SearchOutlined, ErrorOutlineOutlined, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import useSortableTable from '../utils/useSortableTable';

export const HOURS_POSTED_TYPES = ['Retainer', 'Hourly', 'Fixed Price', 'Internal'];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_ROWS_PER_PAGE = 25;

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
  padding: theme.spacing(0),
}));

const LoadingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8),
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const ExpandCell = styled(TableCell)(({ theme }) => ({
  width: theme.spacing(6),
  paddingRight: 0,
}));

const ClientCell = styled(TableCell)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));

const ClientName = styled('span', { shouldForwardProp: (prop) => prop !== 'clickable' })(({ theme, clickable }) => ({
  cursor: clickable ? 'pointer' : 'default',
  color: clickable ? theme.palette.primary.main : 'inherit',
  textDecoration: clickable ? 'underline' : 'none',
  textUnderlineOffset: '2px',
  '&:hover': { color: clickable ? theme.palette.primary.dark : 'inherit' },
}));

const FailedIcon = styled(ErrorOutlineOutlined)(({ theme }) => ({
  color: theme.palette.error.main,
}));

const FailedText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
}));

const TotalCell = styled(TableCell)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
}));

const TotalsRow = styled(TableRow)(({ theme }) => ({
  '& td': {
    borderTop: `2px solid ${theme.palette.divider}`,
    fontWeight: theme.typography.fontWeightBold,
  },
}));

const DetailCell = styled(TableCell)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: 0,
  backgroundColor: theme.palette.action.hover,
}));

const DetailBox = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(6),
}));

const DetailTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

const hours = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const COLUMN_NOTES = {
  Internal: 'Internal work, plus anything logged as non-billable and anything whose billing type resolves to Non Billable',
};
export const monthLabel = (month) => {
  const parsed = dayjs(`${month}-01`);
  return parsed.isValid() ? parsed.format('MMM YYYY') : month;
};

const rangeLabel = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return '';
  const from = start.format('MMM YYYY');
  const to = end.format('MMM YYYY');
  return from === to ? from : `${from} to ${to}`;
};

// Shared so a client with no months hands React.memo the same array on every render.
const NO_MONTHS = [];

const MonthBreakdown = React.memo(({ months }) => (
  <DetailBox>
    <DetailTitle variant="body2">Hours posted by month</DetailTitle>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Month</TableCell>
          {HOURS_POSTED_TYPES.map((type) => (
            <TableCell key={type} align="right">{type}</TableCell>
          ))}
          <TableCell align="right">Total Hours</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {months.map((month) => (
          <TableRow key={month.month}>
            <TableCell>{monthLabel(month.month)}</TableCell>
            {HOURS_POSTED_TYPES.map((type) => (
              <TableCell key={type} align="right">{hours(month.byType?.[type])}</TableCell>
            ))}
            <TotalCell align="right">{hours(month.total)}</TotalCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </DetailBox>
));
MonthBreakdown.displayName = 'MonthBreakdown';

const RunnitHoursPosted = ({ data, loading, hasFetched, failed, startDate, endDate }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [expandedClients, setExpandedClients] = useState(() => new Set());

  const { sorted, order, handleSort } = useSortableTable(
    data,
    {
      clientName: (r) => r.clientName || '',
      Retainer: (r) => r.byType?.Retainer || 0,
      Hourly: (r) => r.byType?.Hourly || 0,
      'Fixed Price': (r) => r.byType?.['Fixed Price'] || 0,
      Internal: (r) => r.byType?.Internal || 0,
      total: (r) => r.total || 0,
    },
    { key: 'total', dir: 'desc' }
  );

  useEffect(() => setPage(0), [data, order]);

  const toggleExpanded = (clientId) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const totals = data.reduce(
    (acc, row) => {
      HOURS_POSTED_TYPES.forEach((type) => {
        acc[type] += row.byType?.[type] || 0;
      });
      acc.total += row.total || 0;
      return acc;
    },
    { Retainer: 0, Hourly: 0, 'Fixed Price': 0, Internal: 0, total: 0 }
  );

  const sortHeader = (key, label) => (
    <TableSortLabel active={order.key === key} direction={order.key === key ? order.dir : 'asc'} onClick={() => handleSort(key)}>
      {label}
    </TableSortLabel>
  );

  return (
    <StyledTabPanel value="hours-posted">
      <Paper>
        <TableContainer>
          {loading ? (
            <LoadingBox><CircularProgress /></LoadingBox>
          ) : (
            <Table stickyHeader>
              <TableHead>
                <TableRow key="header">
                  <ExpandCell />
                  <TableCell sortDirection={order.key === 'clientName' ? order.dir : false}>{sortHeader('clientName', 'Client')}</TableCell>
                  {HOURS_POSTED_TYPES.map((type) => (
                    <TableCell key={type} align="right" sortDirection={order.key === type ? order.dir : false}>
                      {COLUMN_NOTES[type] ? (
                        <Tooltip title={COLUMN_NOTES[type]}>
                          <span>{sortHeader(type, type)}</span>
                        </Tooltip>
                      ) : (
                        sortHeader(type, type)
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="right" sortDirection={order.key === 'total' ? order.dir : false}>{sortHeader('total', 'Total Hours')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                  const isExpanded = expandedClients.has(row.clientId);
                  const months = row.months || NO_MONTHS;
                  return (
                    <React.Fragment key={row.clientId}>
                      <TableRow hover>
                        <ExpandCell>
                          <Tooltip title={isExpanded ? 'Hide month-by-month hours' : 'Show month-by-month hours'}>
                            <span>
                              <IconButton size="small" onClick={() => toggleExpanded(row.clientId)} disabled={months.length === 0}>
                                {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </ExpandCell>
                        <ClientCell>
                          <Tooltip title={row.clientSlug ? `Open the client page for ${row.clientName}` : ''}>
                            <ClientName clickable={!!row.clientSlug} onClick={() => row.clientSlug && navigate(`/client/${row.clientSlug}`)}>
                              {row.clientName}
                            </ClientName>
                          </Tooltip>
                        </ClientCell>
                        {HOURS_POSTED_TYPES.map((type) => (
                          <TableCell key={type} align="right">{hours(row.byType?.[type])}</TableCell>
                        ))}
                        <TotalCell align="right">{hours(row.total)}</TotalCell>
                      </TableRow>
                      <TableRow>
                        <DetailCell colSpan={HOURS_POSTED_TYPES.length + 3}>
                          <Collapse in={isExpanded} unmountOnExit>
                            <MonthBreakdown months={months} />
                          </Collapse>
                        </DetailCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
                {data.length > 0 && (
                  <TotalsRow>
                    <TableCell />
                    <TableCell>All clients</TableCell>
                    {HOURS_POSTED_TYPES.map((type) => (
                      <TableCell key={type} align="right">{hours(totals[type])}</TableCell>
                    ))}
                    <TableCell align="right">{hours(totals.total)}</TableCell>
                  </TotalsRow>
                )}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={HOURS_POSTED_TYPES.length + 3} align="center">
                      {failed ? (
                        <EmptyStateBox>
                          <FailedIcon />
                          <FailedText variant="body1">This report could not be generated, so no hours are shown</FailedText>
                          <Typography variant="body2">Check the months you picked and select Generate Report again.</Typography>
                        </EmptyStateBox>
                      ) : (
                        <EmptyStateBox>
                          <SearchOutlined />
                          <Typography variant="body1">
                            {hasFetched ? `No time was posted in ${rangeLabel(startDate, endDate) || 'the selected months'}` : 'Click Generate Report to load data'}
                          </Typography>
                        </EmptyStateBox>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {!loading && (data.length > rowsPerPage || rowsPerPage !== DEFAULT_ROWS_PER_PAGE) && (
          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        )}
      </Paper>
    </StyledTabPanel>
  );
};

export default RunnitHoursPosted;
